import React, {Fragment, useCallback, useEffect, useState} from "react";
import NavigationLayout from "../../Components/NavigationLayout";
import {Loading, Modal, Page, Select, Toast} from "@shopify/polaris";
import {Button, Card} from "react-bootstrap";
import axios from "../../Assets/Lib/axios";
import Cookies from "js-cookie";
import LoadingBtn from "../../Components/LoadingBtn";
import {isEmailAllowed} from "../../global";
import {MultiSelect} from "react-multi-select-component";

// Extract only the fields expected by the API
const extractUpdatableFields = (event) => ({
    active: event.active,
    show_in_admin: event.show_in_admin,
    start_date: event.start_date,
    end_date: event.end_date,
    is_credential_enabled: event.is_credential_enabled,
    is_credential_mandatory: event.is_credential_mandatory,
    is_reference_code_enabled: event.is_reference_code_enabled ?? 0
});

const AllEvents = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [allEvents, setAllEvents] = useState([]);
    const [showEvent, setShowEvent] = useState({});
    const accreditationTypeId = Cookies.get("accreditation_type_id");
    const [toastActive, setToastActive] = useState(false);
    const [toastContent, setToastContent] = useState("");
    const [toastError, setToastError] = useState(false);
    const [fieldCache, setFieldCache] = useState(null);
    const [activeAreYouSure, setActiveAreYouSure] = useState(false);
    const [questionValue, setQuestionValue] = useState('');
    const [profileOptions, setProfileOptions] = useState([]);
    const [selectedProfiles, setSelectedProfiles] = useState([]);
    const [profilesDirty, setProfilesDirty] = useState(false);
    const [profilesRenderTick, setProfilesRenderTick] = useState(0);

    const toggleToast = useCallback(() => setToastActive((active) => !active), []);

    const toastMarkup = toastActive ? (
        <Toast
            content={toastContent}
            error={toastError}
            onDismiss={toggleToast}
        />
    ) : null;

    const fetchAllEvents = () => {
        setIsLoading(true);
        setShowEvent({});
        axios
            .get(`admin/v1/accreditation-types`)
            .then((res) => {
                const response = res?.data?.data;
                setAllEvents(response);
            })
            .catch((err) => console.log("error :", err))
            .finally(() => setIsLoading(false));
    };

    const syncNewEvents = () => {
        setIsLoading(true);
        setShowEvent({});
        axios
            .post(`admin/v1/accreditation-types/sync-moi`)
            .then((res) => {
                if (res?.data?.success) {
                    fetchAllEvents();
                }
            })
            .catch((err) => console.log("error :", err))
            .finally(() => setIsLoading(false));
    };

    const handleFieldUpdate = (fieldName, value) => {
        const isDateField = fieldName === "start_date" || fieldName === "end_date";
        const formattedValue = isDateField ? `${value}T00:00:00Z` : value;
        const previousValue = showEvent[fieldName];

        const updatedData = {
            ...extractUpdatableFields(showEvent),
            [fieldName]: formattedValue,
        };

        const form_data = new FormData();
        Object.entries(updatedData).forEach(([key, val]) => {
            form_data.append(key, val);
        });

        form_data.append("_method", "PATCH");

        setShowEvent((prev) => ({
            ...prev,
            [fieldName]: formattedValue,
        }));

        axios
            .post(`admin/v1/accreditation-types/${showEvent.id}`, form_data)
            .then((res) => {
                setShowEvent((prev) => ({
                    ...prev,
                    ...res.data,
                }));

                setToastContent(`${fieldName.replace("_", " ")} updated successfully`);
                setToastError(false);
                setToastActive(true);
            })
            .catch((err) => {
                setShowEvent((prev) => ({
                    ...prev,
                    [fieldName]: previousValue,
                }));

                setToastContent(`Failed to update ${fieldName.replace("_", " ")}`);
                setToastError(true);
                setToastActive(true);
            });
    };

    const handleEventSelect = async (selectedId) => {
        const eventId = parseInt(selectedId, 10);

        if (!eventId) return;
        setIsLoading(true);
        setProfileOptions([]);
        setSelectedProfiles([]);
        try {
            const res = await axios.get(`admin/v1/accreditation-types/${eventId}`);
            const event = res?.data?.data;
            if (event) {
                const {opts, selected} = await fetchProfilesFor(event);
                setShowEvent(event);
                setProfileOptions(opts);
                setSelectedProfiles(selected);
                setProfilesRenderTick((tick) => tick + 1);
            } else {
                setIsLoading(false);
            }
        } catch (err) {
            setToastContent("Failed to load event data");
            setToastError(true);
            setToastActive(true);
            setIsLoading(false);
        }
    };

    // Runs after the profiles are committed to the DOM
    useEffect(() => {
        if (profilesRenderTick > 0) {
            setIsLoading(false);
        }
    }, [profilesRenderTick]);

    const handleActivation = (fieldName, value) => {

        if (value === 0) {
            handleFieldUpdate(fieldName, value);
            return;
        }

        setActiveAreYouSure(true);
        const questionString = showEvent?.activation_email_sent === 0
            ? 'Are you sure you want to send an email?'
            : 'Notification has already been sent, are you sure to send another?';
        setQuestionValue(questionString);
        setFieldCache({fieldName, value})
    }

    const notifyEmail = () => {
        axios
            .post(`admin/v1/accreditation-types/notify/${showEvent.id}`)
            .then((res) => {
                setShowEvent(res?.data?.accreditationType);
                setToastContent('Email sent successfully');
                setToastError(false);
                setToastActive(true);
            })
            .catch((err) => {
                setToastContent('Failed to send email');
                setToastError(true);
                setToastActive(true);
            });
    }

    const handleYes = async () => {
        if (!fieldCache) return;

        const {fieldName, value} = fieldCache;
        setActiveAreYouSure(false);

        try {
            await handleFieldUpdate(fieldName, value);
            await notifyEmail();
        } finally {
            setFieldCache(null);
        }
    };

    const handleNo = async () => {
        if (!fieldCache) return;

        const {fieldName, value} = fieldCache;
        setActiveAreYouSure(false);

        try {
            await handleFieldUpdate(fieldName, value);
        } finally {
            setFieldCache(null);
        }
    };

    const eventOptions = allEvents.map((event) => ({
        label: event?.type?.en || `Event #${event.id}`,
        value: event.id.toString(),
    }));

    const parseProfileIds = (value) => {
        if (Array.isArray(value)) return value;
        if (!value) return [];
        return value.toString().split(",").filter((id) => id !== "");
    };

    const fetchProfilesFor = async (event) => {
        const res = await axios.get(`admin/v1/registration-requests/profile-details`, {
            params: {
                accreditationTypeId: event.id,
                allProfiles: true,
            },
        });
        const profiles = res?.data?.data?.profileListDetails?.profiles || [];
        const opts = profiles.map((p) => ({label: p.label, value: p.value}));
        const ids = parseProfileIds(event?.profile_ids);
        return {
            opts,
            selected: opts.filter((opt) => ids.some((id) => +id === +opt.value)),
        };
    };

    const handleProfilesChange = (selected) => {
        setSelectedProfiles(selected);
        setProfilesDirty(true);
    };

    const saveProfiles = (isOpen) => {
        if (isOpen || !profilesDirty) return;
        setProfilesDirty(false);

        const form_data = new FormData();
        Object.entries(extractUpdatableFields(showEvent)).forEach(([key, val]) => {
            form_data.append(key, val);
        });
        form_data.append("profile_ids", selectedProfiles.map((item) => item.value).join(","));
        form_data.append("_method", "PATCH");

        axios
            .post(`admin/v1/accreditation-types/${showEvent.id}`, form_data)
            .then((res) => {
                setShowEvent((prev) => ({
                    ...prev,
                    profile_ids: selectedProfiles.map((item) => item.value).join(","),
                }));
                setToastContent("Profiles updated successfully");
                setToastError(false);
                setToastActive(true);
            })
            .catch((err) => {
                setToastContent("Failed to update profiles");
                setToastError(true);
                setToastActive(true);
            });
    };


    useEffect(() => {
        fetchAllEvents();
    }, [accreditationTypeId]);

    useEffect(() => {
        if (allEvents.length > 0 && accreditationTypeId) {
            const parsedId = parseInt(accreditationTypeId, 10);
            const event = allEvents.find((i) => i?.id === parsedId);
            setShowEvent(event);
            if (event?.id) {
                handleEventSelect(event.id);
            }
        }
    }, [allEvents]);

    const loadingMarkup = isLoading
        ? <Fragment>
            <Loading/>
            <div className="spinnerContainer-x is-loading">
                <div className="vertical-center">{isLoading && <LoadingBtn/>}</div>
            </div>
        </Fragment>
        : null;

    return (
        <NavigationLayout>
            <Page fullWidth>
                <Card>
                    {loadingMarkup}
                    <div className="event-details-container">
                        <div className="event-details-header-wrapper">
                            <div style={{maxWidth: 400, marginBottom: 20}}>
                                <Select
                                    label="Select Event"
                                    options={eventOptions}
                                    onChange={handleEventSelect}
                                    value={showEvent?.id?.toString() || ''}
                                    disabled={isLoading}
                                />
                            </div>

                            <h4>{showEvent?.type?.en}</h4>
                            {isEmailAllowed && <Button
                                variant="outline-primary"
                                size="lg"
                                onClick={syncNewEvents}
                                disabled={isLoading}
                            >
                                {isLoading ? "Syncing..." : "Sync Data"}
                            </Button>}
                        </div>

                        {showEvent ? (
                            <div className="event-grid">
                                <div className="event-row">
                                    <span className="label"><b>Active:</b></span>
                                    <label className="switch">
                                        <input
                                            type="checkbox"
                                            checked={!!showEvent.active}
                                            onChange={(e) =>
                                                handleActivation("active", e.target.checked ? 1 : 0)
                                            }
                                        />
                                        <span className="slider round"></span>
                                    </label>
                                </div>

                                <div className="event-row">
                                    <span className="label"><b>Show In Admin:</b></span>
                                    <label className="switch">
                                        <input
                                            type="checkbox"
                                            checked={!!showEvent.show_in_admin}
                                            onChange={(e) =>
                                                handleFieldUpdate("show_in_admin", e.target.checked ? 1 : 0)
                                            }
                                        />
                                        <span className="slider round"></span>
                                    </label>
                                </div>

                                <div className="event-row">
                                    <span className="label"><b>Start Date:</b></span>
                                    <input
                                        type="date"
                                        value={
                                            showEvent.start_date
                                                ? showEvent.start_date.slice(0, 10)
                                                : ""
                                        }
                                        onChange={(e) =>
                                            handleFieldUpdate("start_date", e.target.value)
                                        }
                                    />
                                </div>

                                <div className="event-row">
                                    <span className="label"><b>End Date:</b></span>
                                    <input
                                        type="date"
                                        value={
                                            showEvent.end_date
                                                ? showEvent.end_date.slice(0, 10)
                                                : ""
                                        }
                                        onChange={(e) =>
                                            handleFieldUpdate("end_date", e.target.value)
                                        }
                                    />
                                </div>

                                <div className="event-row">
                  <span className="label">
                    <b>Registration Reference Number:</b>
                    <br/>
                      If Registration Reference Number Mandatory, and this is enabled then
                      it will be validated with the list of voucher codes.
                  </span>
                                    <label className="switch">
                                        <input
                                            type="checkbox"
                                            checked={!!showEvent.is_credential_enabled}
                                            onChange={(e) =>
                                                handleFieldUpdate(
                                                    "is_credential_enabled",
                                                    e.target.checked ? 1 : 0
                                                )
                                            }
                                        />
                                        <span className="slider round"></span>
                                    </label>
                                </div>

                                <div className="event-row">
                  <span className="label">
                    <b>Registration Reference Number Mandatory?:</b>
                  </span>
                                    <label className="switch">
                                        <input
                                            type="checkbox"
                                            checked={!!showEvent.is_reference_code_enabled}
                                            onChange={(e) =>
                                                handleFieldUpdate(
                                                    "is_reference_code_enabled",
                                                    e.target.checked ? 1 : 0
                                                )
                                            }
                                        />
                                        <span className="slider round"></span>
                                    </label>
                                </div>

                                <div className="event-row">
                                    <span className="label"><b>Credentials File Required:</b> A credentials file must be provided.</span>
                                    <label className="switch">
                                        <input
                                            type="checkbox"
                                            checked={!!showEvent.is_credential_mandatory}
                                            onChange={(e) =>
                                                handleFieldUpdate(
                                                    "is_credential_mandatory",
                                                    e.target.checked ? 1 : 0
                                                )
                                            }
                                        />
                                        <span className="slider round"></span>
                                    </label>
                                </div>

                                {false &&
                                    <div className="event-row">
                                        <span className="label"><b>Is export enable?</b></span>
                                        <label className="switch">
                                            <input
                                                type="checkbox"
                                                checked={!!showEvent.is_export_enable}
                                                onChange={(e) =>
                                                    handleFieldUpdate(
                                                        "is_export_enable",
                                                        e.target.checked ? 1 : 0
                                                    )
                                                }
                                            />
                                            <span className="slider round"></span>
                                        </label>
                                    </div>
                                }

                                {!!showEvent.is_accreditation &&
                                    <div className="event-row">
                                        <span className="label">
                                            <b>Profiles:</b> Selected profiles will be used for filtering.
                                        </span>
                                        <div className="profiles-multiselect" style={{minWidth: 300}}>
                                            <MultiSelect
                                                options={profileOptions}
                                                value={selectedProfiles}
                                                onChange={handleProfilesChange}
                                                onMenuToggle={saveProfiles}
                                                labelledBy="Select Profiles"
                                            />
                                        </div>
                                    </div>
                                }

                                <Modal open={activeAreYouSure}>
                                    <Modal.Section>
                                        <div className="popupBlackText"
                                             style={{marginBottom: "4rem", fontSize: "1.6rem"}}>
                                            {questionValue}
                                        </div>

                                        <div className="rowDiv"
                                             style={{float: "right", marginTop: "3rem", marginBottom: "2rem"}}>
                                            <button className="button buttonApprove" onClick={handleYes}>
                                                Yes
                                            </button>
                                            <button className="button buttonEmail" onClick={handleNo}>
                                                No
                                            </button>
                                        </div>
                                    </Modal.Section>
                                </Modal>


                            </div>
                        ) : (
                            <div>Loading event data...</div>
                        )}
                    </div>
                    {toastMarkup}
                </Card>
            </Page>
        </NavigationLayout>
    );
};

export default AllEvents;
